
import { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Activity, 
  RefreshCw,
  Download,
  ChevronsUpDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import StockChart from '../components/StockChart';
import { stockDatasets } from '../data/sampleData';
import { formatPercentChange, formatCurrency } from '../utils/stockUtils';
import { cn } from '@/lib/utils';
import DataTable from './DataTable';
import DatasetSelector from './DatasetSelector';
import { toast } from 'sonner';

const AnalysisTab = () => {
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([stockDatasets[0].id]);
  const [timeframe, setTimeframe] = useState<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'>('3M');
  const [analysisType, setAnalysisType] = useState('technical');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [csvData, setCsvData] = useState<string | null>(null);

  const selectedStock = stockDatasets.find(s => s.id === selectedDatasets[0]) || stockDatasets[0];

  const handleDatasetSelection = (selectedIds: string[]) => {
    if (selectedIds.length === 0) {
      return;
    }
    setSelectedDatasets(selectedIds);
  };
  
  const handleTimeframeChange = (newTimeframe: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All') => {
    setTimeframe(newTimeframe);
  };

  const runAnalysis = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      setAnalysisResults({
        correlation: Math.random() * 0.5 + 0.3,
        volatility: Math.random() * 15 + 5,
        sharpeRatio: Math.random() * 1.5 + 0.5,
        betaToMarket: Math.random() * 0.4 + 0.8,
        rSquared: Math.random() * 0.3 + 0.6,
        trendStrength: Math.random() * 50 + 30,
        tableData: generateAnalysisTableData()
      });
      setIsLoading(false);
      toast.success("Analysis completed successfully!");
    }, 1500);
  };

  const generateAnalysisTableData = () => {
    const data = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    for (let i = 0; i < 30; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        price: (selectedStock.currentValue * (1 + (Math.random() * 0.1 - 0.05))).toFixed(2),
        volume: Math.floor(Math.random() * 10000000) + 1000000,
        rsi: (Math.random() * 30 + 35).toFixed(2),
        macd: (Math.random() * 2 - 1).toFixed(3),
        signal: (Math.random() * 2 - 1).toFixed(3),
        histogram: (Math.random() * 1 - 0.5).toFixed(3),
      });
    }
    
    return data;
  };

  useEffect(() => {
    if (analysisResults?.tableData) {
      const headers = Object.keys(analysisResults.tableData[0]).join(',');
      const rows = analysisResults.tableData.map((row: any) => 
        Object.values(row).join(',')
      ).join('\n');
      
      setCsvData(`${headers}\n${rows}`);
    }
  }, [analysisResults]);

  const downloadCSV = () => {
    if (!csvData) return;
    
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedStock.name}_analysis.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV downloaded successfully!");
  };

  return (
    <div className="space-y-8">
      <div className="bg-card border rounded-xl p-6 space-y-6">
        <h2 className="text-2xl font-semibold">Technical Analysis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Select Datasets</h3>
            <DatasetSelector 
              onSelectionChange={handleDatasetSelection}
              selectedDatasets={selectedDatasets}
            />
          </div>
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Analysis Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm">Timeframe</label>
                <select 
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={timeframe}
                  onChange={(e) => handleTimeframeChange(e.target.value as any)}
                >
                  <option value="1W">1 Week</option>
                  <option value="1M">1 Month</option>
                  <option value="3M">3 Months</option>
                  <option value="6M">6 Months</option>
                  <option value="1Y">1 Year</option>
                  <option value="All">All Time</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm">Analysis Type</label>
                <select 
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={analysisType}
                  onChange={(e) => setAnalysisType(e.target.value)}
                >
                  <option value="technical">Technical</option>
                  <option value="fundamental">Fundamental</option>
                  <option value="sentiment">Sentiment</option>
                  <option value="combined">Combined</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex items-end">
            <Button 
              className="w-full"
              onClick={runAnalysis}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>Run Analysis</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="col-span-2">
          <StockChart 
            stockData={selectedStock} 
            height={400} 
            externalTimeframe={timeframe}
            onTimeframeChange={handleTimeframeChange}
          />
          
          {selectedDatasets.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedDatasets.slice(1).map(datasetId => {
                const dataset = stockDatasets.find(d => d.id === datasetId);
                if (!dataset) return null;
                return (
                  <div key={datasetId} className="w-full md:w-[calc(50%-0.5rem)]">
                    <StockChart 
                      stockData={dataset} 
                      height={250} 
                      minimal={true}
                      externalTimeframe={timeframe}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="bg-card border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Analysis Results</h3>
            {analysisResults && (
              <Button size="sm" variant="outline" onClick={downloadCSV} className="flex items-center gap-1">
                <Download size={14} />
                <span>Export</span>
              </Button>
            )}
          </div>
          
          {!analysisResults ? (
            <div className="text-center py-8 text-muted-foreground">
              Run analysis to see results
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Correlation</span>
                <span className="text-sm">{analysisResults.correlation.toFixed(3)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Volatility</span>
                <span className="text-sm">{analysisResults.volatility.toFixed(2)}%</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Sharpe Ratio</span>
                <span className="text-sm">{analysisResults.sharpeRatio.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Beta to Market</span>
                <span className="text-sm">{analysisResults.betaToMarket.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">R-Squared</span>
                <span className="text-sm">{analysisResults.rSquared.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Trend Strength</span>
                <span className={cn(
                  "text-sm",
                  analysisResults.trendStrength > 50 ? "text-green-600" : "text-red-600"
                )}>
                  {analysisResults.trendStrength.toFixed(1)}
                </span>
              </div>
              
              <div className="pt-4">
                <div className="text-sm font-medium mb-2">Analysis Summary</div>
                <p className="text-sm text-muted-foreground">
                  The {selectedStock.name} shows {analysisResults.trendStrength > 50 ? "strong positive" : "negative"} trend characteristics with a {analysisResults.volatility > 10 ? "high" : "moderate"} volatility profile. The correlation with broader market indices suggests {analysisResults.correlation > 0.5 ? "significant" : "moderate"} market influence on price movements.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {analysisResults && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Mean Reversion</span>
                <Activity size={16} className="text-primary" />
              </div>
              <div className="text-xl font-semibold mb-1">
                {Math.random() > 0.5 ? "High" : "Low"}
              </div>
              <p className="text-xs text-muted-foreground">
                {Math.random() > 0.5 
                  ? "Price is likely to revert to the mean"
                  : "Price is trending away from the mean"}
              </p>
            </div>
            
            <div className="bg-card border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Momentum</span>
                <TrendingUp size={16} className="text-primary" />
              </div>
              <div className="text-xl font-semibold mb-1">
                {Math.random() > 0.5 ? "Strong" : "Weak"}
              </div>
              <p className="text-xs text-muted-foreground">
                {Math.random() > 0.5 
                  ? "Strong price momentum indicates continued trend"
                  : "Weak momentum suggests potential trend reversal"}
              </p>
            </div>
            
            <div className="bg-card border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Support/Resistance</span>
                <ChevronsUpDown size={16} className="text-primary" />
              </div>
              <div className="text-xl font-semibold mb-1">
                {formatCurrency(selectedStock.currentValue * (1 - Math.random() * 0.1))}
              </div>
              <p className="text-xs text-muted-foreground">
                Next significant support level
              </p>
            </div>
            
            <div className="bg-card border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Market Sentiment</span>
                <Activity size={16} className="text-primary" />
              </div>
              <div className="text-xl font-semibold mb-1">
                {Math.random() > 0.5 ? "Bullish" : "Bearish"}
              </div>
              <p className="text-xs text-muted-foreground">
                Based on technical indicators and volume analysis
              </p>
            </div>
          </div>

          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Technical Analysis Data</h3>
              <Button size="sm" variant="outline" onClick={downloadCSV} className="flex items-center gap-1">
                <Download size={14} />
                <span>Export CSV</span>
              </Button>
            </div>
            <div className="p-4 overflow-x-auto">
              <DataTable data={analysisResults.tableData} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalysisTab;
